/**
 * src/engine/projection.ts — the month-by-month borrow-path engine (§6.4–5.13).
 *
 * Pure and deterministic. The single-portfolio and bucket modes share one code path
 * (the single portfolio is one bucket). Inputs are assumed already validated by
 * `scenarioInputsSchema` (§8.2); `containsNonFinite()` is the defence-in-depth guard.
 */
import type {
  MonthlyRow,
  ProjectionSummary,
  ScenarioInputs,
} from '@/types';
import { addMonths, getDaysInMonth, startOfMonth } from 'date-fns';
import {
  aggregateLVC,
  aggregatePortfolioValue,
  cureWithModel,
  isMaintenanceCall,
  type BucketState,
} from './collateral';
import { buildGainsTaxModel } from './gainsTax';
import { advancePortfolio, monthlyReturnFromAnnual, reinvestCash } from './portfolio';
import { afterTaxApr, aprFromBaseRate } from './rates';
import { resolveBaseRate } from './ratePaths';
import { gainFraction } from './tax';

export interface ProjectionOptions {
  /** Start month (truncated to the 1st). Defaults to the current month. Injected in tests. */
  startDate?: Date;
  /** Monte Carlo: per-month per-bucket MONTHLY returns. Overrides deterministic growth. */
  returnSampler?: (monthIndex: number, states: BucketState[]) => number[];
  /** Monte Carlo: per-month SOFR (stochastic path). Overrides the deterministic rate path. */
  baseRateSampler?: (monthIndex: number, prevSofr: number) => number;
}

export interface BorrowPathResult {
  rows: MonthlyRow[];
  summary: ProjectionSummary;
  /** Terminal bucket states (used by the estate view). */
  finalStates: BucketState[];
  effectiveSpread: number;
}

/** Build initial bucket states from inputs (single portfolio = one synthetic bucket). */
export function buildInitialStates(inputs: ScenarioInputs): BucketState[] {
  if (inputs.useBuckets && inputs.collateralBuckets.length > 0) {
    return inputs.collateralBuckets.map((b) => ({
      assetClass: b.assetClass,
      value: b.value,
      basis: b.costBasis,
      advanceRate: b.advanceRate,
      eligible: b.eligible,
      expectedReturnAnnualPct: b.expectedReturnAnnualPct,
      volatilityPct: b.volatilityPct,
      dividendYieldAnnualPct: b.dividendYieldAnnualPct,
    }));
  }
  const basis =
    inputs.costBasis !== null ? inputs.costBasis : (inputs.portfolioValue * inputs.costBasisPct) / 100;
  // A loan-value override is modeled as an advance rate that reproduces it at origination
  // and scales with portfolio growth (§6.2).
  let advanceRate = inputs.blendedAdvanceRate;
  if (inputs.loanValueOverride !== null && inputs.portfolioValue > 0) {
    advanceRate = inputs.loanValueOverride / inputs.portfolioValue;
  }
  return [
    {
      assetClass: 'broad_etf_large_cap',
      value: inputs.portfolioValue,
      basis,
      advanceRate,
      eligible: true,
      expectedReturnAnnualPct: inputs.portfolioExpectedReturnAnnualPct,
      volatilityPct: 0,
      dividendYieldAnnualPct: inputs.dividendYieldAnnualPct,
    },
  ];
}

/** Sum scheduled amounts into a per-month array of length `months`. */
export function scheduleToArray(
  schedule: { monthIndex: number; amount?: number; pctChange?: number }[],
  months: number,
  key: 'amount' | 'pctChange' = 'amount',
): number[] {
  const arr = new Array<number>(months).fill(0);
  for (const item of schedule) {
    const v = (key === 'amount' ? item.amount : item.pctChange) ?? 0;
    if (item.monthIndex >= 0 && item.monthIndex < months) arr[item.monthIndex]! += v;
  }
  return arr;
}

/** Expand the draw schedule (frequency + duration + lump draws) into per-month draws (§6.8). */
export function buildDrawArray(inputs: ScenarioInputs, months: number): number[] {
  const arr = new Array<number>(months).fill(0);
  const start = inputs.drawStartMonth;
  const lastMonth = months - 1;
  const end =
    inputs.drawDurationMonths === null
      ? lastMonth
      : Math.min(lastMonth, start + inputs.drawDurationMonths - 1);
  if (inputs.drawFrequency === 'one_time') {
    if (start <= lastMonth) arr[start]! += inputs.drawAmount;
  } else {
    const step = inputs.drawFrequency === 'monthly' ? 1 : inputs.drawFrequency === 'quarterly' ? 3 : 12;
    for (let m = start; m <= end; m += step) {
      if (m >= 0 && m <= lastMonth) arr[m]! += inputs.drawAmount;
    }
  }
  for (const lump of inputs.lumpDraws) {
    if (lump.monthIndex >= 0 && lump.monthIndex < months) arr[lump.monthIndex]! += lump.amount;
  }
  return arr;
}

/** Required payment for a month under the active mode (§6.6); extra payments add on top. */
export function computePayment(
  inputs: ScenarioInputs,
  monthIndex: number,
  interest: number,
  balance: number,
  customArr: number[],
  extraArr: number[],
): number {
  let base: number;
  switch (inputs.paymentMode) {
    case 'interest_only':
      base = interest;
      break;
    case 'fixed':
      base = Math.min(inputs.fixedPayment ?? 0, balance + interest);
      break;
    case 'capitalize':
      base = 0;
      break;
    case 'custom':
      base = customArr[monthIndex] ?? 0;
      break;
  }
  return base + (extraArr[monthIndex] ?? 0);
}

/** Run the borrow-path projection → monthly rows + summary (§6.4). */
export function runProjection(
  inputs: ScenarioInputs,
  opts: ProjectionOptions = {},
): BorrowPathResult {
  const months = inputs.years * 12;
  const startMonth = startOfMonth(opts.startDate ?? new Date());

  const states = buildInitialStates(inputs);
  // The borrow path's own tax world: forced liquidations stack within a tax year, and
  // never share a position with the sell-path counterfactual (§6.12b).
  const gainsTax = buildGainsTaxModel(inputs, startMonth.getFullYear());
  const proceedsMonthlyReturn = monthlyReturnFromAnnual(inputs.proceedsReturnAnnualPct);

  const drawArr = buildDrawArray(inputs, months);
  const contributionArr = scheduleToArray(inputs.contributions, months);
  const customArr = scheduleToArray(inputs.customPayments, months);
  const extraArr = scheduleToArray(inputs.extraPayments, months);
  const shockArr = scheduleToArray(inputs.collateralShocks, months, 'pctChange');

  let balance = inputs.initialBalance;
  let deployedProceeds = 0;
  let cumulativeInterest = 0;
  let cumulativeDraws = 0;
  let cumulativePayments = 0;
  let cumulativeTax = 0;

  const rows: MonthlyRow[] = [];
  let startApr = 0;
  let lastApr = 0;
  let startSpread = 0;
  let prevBaseRate = inputs.ratePath.baseRatePct;

  for (let m = 0; m < months; m++) {
    const date = addMonths(startMonth, m);
    const baseRate = opts.baseRateSampler
      ? Math.max(0, opts.baseRateSampler(m, prevBaseRate))
      : resolveBaseRate(inputs.ratePath, m);
    prevBaseRate = baseRate;

    const startBalance = balance;

    // Step 5: portfolio update (growth, dividends, contributions, shocks) + deployed proceeds.
    if (inputs.proceedsDeployed) deployedProceeds *= 1 + proceedsMonthlyReturn;
    const growth = advancePortfolio(states, {
      contributionThisMonth: contributionArr[m]!,
      shockPctThisMonth: shockArr[m]!,
      dividendHandling: inputs.dividendHandling,
      monthlyReturnOverride: opts.returnSampler ? opts.returnSampler(m, states) : undefined,
    });

    // Steps 7–8: draws.
    const draw = drawArr[m]!;
    balance += draw;
    if (inputs.proceedsDeployed) deployedProceeds += draw;

    // Rate this month: the lender's spread is set at origination and does not re-tier.
    const spread = inputs.spreadPct;
    const apr = aprFromBaseRate(baseRate, spread);
    const afterTax = afterTaxApr(apr, inputs.tax);
    if (m === 0) {
      startApr = apr;
      startSpread = spread;
    }
    lastApr = apr;

    // Step 9: interest (actual calendar days / day-count basis).
    const days = getDaysInMonth(date);
    const interest = balance * (apr / 100) * (days / inputs.dayCountBasis);

    // Step 10–11: payment and new balance.
    const payment = computePayment(inputs, m, interest, balance, customArr, extraArr);
    balance = Math.max(0, balance + interest - payment);

    // 'service_interest' dividends fund interest; any surplus is reinvested (§6.9).
    if (inputs.dividendHandling === 'service_interest') {
      reinvestCash(states, Math.max(0, growth.dividendCashForServicing - interest));
    }

    // Step 12: maintenance check + optional forced-liquidation cure (§6.7).
    let lvc = aggregateLVC(states);
    let forcedSale = 0;
    let realizedTaxThisMonth = 0;
    const callTriggered = isMaintenanceCall(balance, lvc, inputs.minimumLoanValue);
    if (callTriggered && inputs.modelForcedLiquidation) {
      gainsTax.setYear(date.getFullYear());
      const cure = cureWithModel(
        states,
        balance,
        inputs.cureTargetLtv,
        gainsTax,
        inputs.liquidationPolicy,
      );
      balance = cure.balance;
      forcedSale = cure.forcedSale;
      realizedTaxThisMonth = cure.realizedTax;
      lvc = aggregateLVC(states);
    }

    const portfolioValue = aggregatePortfolioValue(states);
    const basis = states.reduce((sum, b) => sum + b.basis, 0);
    const gf = gainFraction(basis, portfolioValue);
    const ltv = lvc > 0 ? balance / lvc : Infinity;
    const marginOfSafety = lvc - balance;
    const callCushionPct = lvc > 0 ? Math.max(0, 1 - ltv) : 0;
    const netWorth = portfolioValue - balance + (inputs.proceedsDeployed ? deployedProceeds : 0);

    cumulativeInterest += interest;
    cumulativeDraws += draw;
    cumulativePayments += payment;
    cumulativeTax += realizedTaxThisMonth;

    rows.push({
      monthIndex: m,
      date: date.toISOString().slice(0, 10),
      baseRate,
      apr,
      afterTaxApr: afterTax,
      drawThisMonth: draw,
      paymentThisMonth: payment,
      interestThisMonth: interest,
      startBalance,
      endBalance: balance,
      portfolioValue,
      basis,
      gainFraction: gf,
      deployedProceeds: inputs.proceedsDeployed ? deployedProceeds : 0,
      netWorth,
      loanValueOfCollateral: lvc,
      ltv,
      marginOfSafety,
      callCushionPct,
      maintenanceCall: callTriggered,
      forcedSale,
      realizedTaxThisMonth,
      cumulativeInterest,
      cumulativeDraws,
      cumulativePayments,
      cumulativeTax,
    });
  }

  const summary = summarize(rows, {
    spread: startSpread,
    startApr,
    endApr: lastApr,
    portfolioExpectedReturnPct: inputs.portfolioExpectedReturnAnnualPct,
    tax: inputs.tax,
  });

  return { rows, summary, finalStates: states, effectiveSpread: startSpread };
}

function summarize(
  rows: MonthlyRow[],
  ctx: {
    spread: number;
    startApr: number;
    endApr: number;
    portfolioExpectedReturnPct: number;
    tax: ScenarioInputs['tax'];
  },
): ProjectionSummary {
  // Called only from runProjection, where years ≥ 1 ⇒ at least 12 rows.
  const last = rows[rows.length - 1]!;
  let worstLtv = 0;
  let minCallCushionPct = Infinity;
  let firstMaintenanceCallMonth: number | null = null;
  for (const r of rows) {
    if (r.ltv > worstLtv) worstLtv = r.ltv;
    if (r.callCushionPct < minCallCushionPct) minCallCushionPct = r.callCushionPct;
    if (r.maintenanceCall && firstMaintenanceCallMonth === null) firstMaintenanceCallMonth = r.monthIndex;
  }
  const afterTax = afterTaxApr(ctx.startApr, ctx.tax);
  return {
    effectiveSpread: ctx.spread,
    startApr: ctx.startApr,
    endApr: ctx.endApr,
    afterTaxApr: afterTax,
    endingBalance: last.endBalance,
    endingPortfolio: last.portfolioValue,
    endingNetWorth: last.netWorth,
    totalInterest: last.cumulativeInterest,
    totalDraws: last.cumulativeDraws,
    totalPayments: last.cumulativePayments,
    totalRealizedTax: last.cumulativeTax,
    worstLtv,
    minCallCushionPct,
    firstMaintenanceCallMonth,
    breakEvenReturnPct: afterTax,
    carryIsAccretive: ctx.portfolioExpectedReturnPct > afterTax,
  };
}

/**
 * Defence-in-depth guard (§8.2 boundary 4): returns the names of any non-finite fields.
 * `ltv` is allowed to be +Infinity (zero-LVC → "n/a"); everything else must be finite.
 */
export function containsNonFinite(result: BorrowPathResult): string[] {
  const bad: string[] = [];
  for (const r of result.rows) {
    for (const [k, v] of Object.entries(r)) {
      if (typeof v !== 'number') continue;
      if (Number.isNaN(v)) bad.push(`row[${r.monthIndex}].${k}=NaN`);
      else if (!Number.isFinite(v) && k !== 'ltv') bad.push(`row[${r.monthIndex}].${k}=Infinity`);
    }
  }
  for (const [k, v] of Object.entries(result.summary)) {
    if (typeof v !== 'number') continue;
    if (Number.isNaN(v)) bad.push(`summary.${k}=NaN`);
    else if (!Number.isFinite(v) && k !== 'worstLtv') bad.push(`summary.${k}=Infinity`);
  }
  return bad;
}
